import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { getEcho } from '../../services/echo';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const HEARTBEAT_INTERVAL = 25000; // 25 s — well within 30 s server window
const SYNC_INTERVAL      = 30000; // Re-sync with backend every 30 s

// ─── Token helpers ────────────────────────────────────────────────────────────
const tokenKey = (eventId) => `queue_token_${eventId}`;

function saveToken(eventId, token) {
  try { localStorage.setItem(tokenKey(eventId), token); } catch (_) {}
}

function loadToken(eventId) {
  try { return localStorage.getItem(tokenKey(eventId)); } catch (_) { return null; }
}

function clearToken(eventId) {
  try { localStorage.removeItem(tokenKey(eventId)); } catch (_) {}
}

// ─── Countdown display ────────────────────────────────────────────────────────
function formatTime(seconds) {
  if (seconds <= 0) return '00:00';
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function formatWait(seconds) {
  if (!seconds || seconds <= 0) return 'Soon';
  if (seconds < 60) return `~${seconds}s`;
  const mins = Math.ceil(seconds / 60);
  return `~${mins} minute${mins !== 1 ? 's' : ''}`;
}

// ─── Main component ───────────────────────────────────────────────────────────
const WaitingRoom = () => {
  const { id: eventId } = useParams();
  const navigate        = useNavigate();
  const { user }        = useAuth();

  const [phase, setPhase]           = useState('loading');  // loading|waiting|admitted|expired|error|queue_disabled
  const [queueData, setQueueData]   = useState(null);
  const [countdown, setCountdown]   = useState(0);
  const [eventTitle, setEventTitle] = useState('');

  const heartbeatRef  = useRef(null);
  const phaseRef      = useRef('loading');   // mirror of `phase` for Echo listeners (avoids stale closures)
  const syncRef       = useRef(null);
  const countdownRef  = useRef(null);
  const echoRef       = useRef(null);
  const tokenRef      = useRef(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const applyStatus = useCallback((data) => {
    if (!data) {
      setPhase('error');
      return;
    }
    if (!data.queue_enabled) {
      setPhase('queue_disabled');
      return;
    }
    if (data.status === 'expired' || data.status === 'cancelled') {
      clearToken(eventId);
      setPhase('expired');
      return;
    }
    if (data.status === 'completed') {
      clearToken(eventId);
      navigate(`/events/${eventId}`, { replace: true });
      return;
    }
    if (data.status === 'admitted' || data.status === 'purchasing') {
      setQueueData(data);
      const remaining = data.seconds_remaining ?? 0;
      setCountdown(remaining);
      setPhase('admitted');
      return;
    }
    // waiting
    setQueueData(data);
    setPhase('waiting');
  }, [eventId, navigate]);

  const syncWithBackend = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;

    try {
      const params = token ? { session_token: token } : {};
      const res = await api.get(`/events/${eventId}/queue/me`, { params });
      if (res.data.in_queue && res.data.data) {
        applyStatus(res.data.data);
      }
    } catch (_) {}
  }, [eventId, applyStatus]);

  // ── Countdown ticker ───────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'admitted') {
      clearInterval(countdownRef.current);
      return;
    }

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          // Re-sync to let backend confirm expiry
          syncWithBackend();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownRef.current);
  }, [phase, syncWithBackend]);

  // ── Pusher subscription ────────────────────────────────────────────────────
  const subscribeEcho = useCallback(() => {
    if (!user) return;

    try {
      const echo = getEcho();
      echoRef.current = echo;

      // Only react to events addressed to the logged-in user (defence in depth —
      // the channel is already private, but a payload user_id mismatch means
      // the message is not for this user)
      const isForMe = (e) => e.user_id === undefined || parseInt(e.user_id) === parseInt(user.id);

      echo.private(`queue.${user.id}`)
        .listen('.queue.position.updated', (e) => {
          if (!isForMe(e) || parseInt(e.event_id) !== parseInt(eventId)) return;
          setQueueData(prev => prev ? {
            ...prev,
            position:               e.position,
            people_ahead:           e.people_ahead,
            estimated_wait_seconds: e.estimated_wait_seconds,
          } : prev);
        })
        .listen('.queue.admitted', (e) => {
          if (!isForMe(e) || parseInt(e.event_id) !== parseInt(eventId)) return;
          const alreadyAdmitted = phaseRef.current === 'admitted';
          setCountdown(e.seconds_remaining ?? 0);
          setQueueData(prev => ({
            ...(prev || {}),
            status:             'admitted',
            session_expires_at: e.session_expires_at,
            seconds_remaining:  e.seconds_remaining,
          }));
          setPhase('admitted');
          if (!alreadyAdmitted) {
            toast.success("You've been admitted! Complete your purchase before the timer runs out.");
          }
        })
        .listen('.queue.session.expired', (e) => {
          if (!isForMe(e) || parseInt(e.event_id) !== parseInt(eventId)) return;
          // Only relevant while actually waiting/admitted — ignore stale expiry
          // broadcasts after the user already left, completed, or expired
          if (!['waiting', 'admitted'].includes(phaseRef.current)) return;
          clearToken(eventId);
          setPhase('expired');
          toast.error('Your purchase session expired.');
        });
    } catch (err) {
      console.warn('WaitingRoom: Pusher subscription failed', err);
    }
  }, [user, eventId]);

  // ── Mount: join or restore queue ───────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      navigate(`/login?from=/events/${eventId}/queue`, { replace: true });
      return;
    }

    // Load event title
    api.get(`/events/${eventId}`).then(r => {
      const ev = r.data?.data || r.data;
      setEventTitle(ev?.title || '');
    }).catch(() => {});

    const existingToken = loadToken(eventId);
    tokenRef.current = existingToken;

    const body = existingToken ? { session_token: existingToken } : {};

    api.post(`/events/${eventId}/queue/join`, body)
      .then(res => {
        const data = res.data?.data;
        if (!data) {
          setPhase('queue_disabled');
          return;
        }
        if (data.session_token) {
          tokenRef.current = data.session_token;
          saveToken(eventId, data.session_token);
        }
        applyStatus(data);
        subscribeEcho();
      })
      .catch(err => {
        const msg = err.response?.data?.message;
        toast.error(msg || 'Failed to join queue.');
        setPhase('error');
      });

    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(syncRef.current);
      clearInterval(countdownRef.current);
      if (echoRef.current && user) {
        try { echoRef.current.leave(`queue.${user.id}`); } catch (_) {}
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, user]);

  // ── Heartbeat + periodic sync ──────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'loading' || phase === 'error' || phase === 'queue_disabled') return;

    clearInterval(heartbeatRef.current);
    clearInterval(syncRef.current);

    heartbeatRef.current = setInterval(async () => {
      const token = tokenRef.current;
      if (!token) return;
      try {
        await api.post(`/events/${eventId}/queue/heartbeat`, { session_token: token });
      } catch (_) {}
    }, HEARTBEAT_INTERVAL);

    syncRef.current = setInterval(syncWithBackend, SYNC_INTERVAL);

    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(syncRef.current);
    };
  }, [phase, eventId, syncWithBackend]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleLeave = async () => {
    const token = tokenRef.current;
    if (token) {
      try { await api.post(`/events/${eventId}/queue/leave`, { session_token: token }); } catch (_) {}
    }
    clearToken(eventId);
    navigate(`/events/${eventId}`, { replace: true });
  };

  const handleProceedToPurchase = () => {
    navigate(`/events/${eventId}`, {
      replace: true,
      state: {
        fromQueue: true,
        session_expires_at: queueData?.session_expires_at,
      },
    });
  };

  const handleRejoin = () => {
    clearToken(eventId);
    window.location.reload();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Checking your place in the queue…</p>
        </div>
      </div>
    );
  }

  if (phase === 'queue_disabled') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4">No queue active</h2>
          <p className="text-gray-600 mb-6">The queue is not enabled for this event.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/events/${eventId}`)}>
            Back to Event
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">We could not place you in the queue. Please try again.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/events/${eventId}`)}>
            Back to Event
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⏰</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h2>
          <p className="text-gray-600 mb-8">
            Your purchase window has ended. Rejoin the queue to try again.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              className="px-6 py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition"
              onClick={handleRejoin}
            >
              Rejoin Queue
            </button>
            <button
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition"
              onClick={() => navigate(`/events/${eventId}`)}
            >
              Back to Event
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'admitted') {
    const isUrgent = countdown <= 60;
    const isDanger = countdown <= 30;
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">You're in!</h2>
          {eventTitle && (
            <p className="text-gray-500 text-sm mb-6">{eventTitle}</p>
          )}

          <p className="text-gray-600 mb-3">Complete your purchase before the timer runs out.</p>

          <div className={`text-6xl font-mono font-bold mb-8 ${isDanger ? 'text-red-500 animate-pulse' : isUrgent ? 'text-orange-500' : 'text-purple-600'}`}>
            {formatTime(countdown)}
          </div>

          {countdown <= 0 && (
            <p className="text-red-500 text-sm mb-4">
              Your session may have expired. Please check with the backend.
            </p>
          )}

          <button
            className="w-full py-4 bg-purple-500 text-white font-bold text-lg rounded-xl hover:bg-purple-600 transition mb-4"
            onClick={handleProceedToPurchase}
            disabled={countdown <= 0}
          >
            Proceed to Purchase
          </button>
          <button
            className="w-full py-2 text-gray-500 text-sm hover:text-gray-700 transition"
            onClick={handleLeave}
          >
            Cancel &amp; leave queue
          </button>
        </div>
      </div>
    );
  }

  // Waiting phase
  const position        = queueData?.position ?? '?';
  const peopleAhead     = queueData?.people_ahead ?? 0;
  const estimatedWait   = queueData?.estimated_wait_seconds;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
        {/* Animated queue icon */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-4 border-purple-200 animate-ping opacity-50" />
          <div className="relative w-20 h-20 rounded-full bg-purple-500 flex items-center justify-center text-white text-3xl">
            🎫
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-1">You're in the Queue</h2>
        {eventTitle && <p className="text-gray-500 text-sm mb-6">{eventTitle}</p>}

        {/* Position card */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Your Position</p>
              <p className="text-4xl font-bold text-purple-600">#{position}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">People Ahead</p>
              <p className="text-4xl font-bold text-gray-800">{peopleAhead}</p>
            </div>
          </div>

          {estimatedWait != null && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Estimated Wait</p>
              <p className="text-2xl font-semibold text-gray-700">{formatWait(estimatedWait)}</p>
            </div>
          )}
        </div>

        <p className="text-gray-500 text-sm mb-6">
          Please keep this page open. You'll be notified automatically when it's your turn.
        </p>

        {/* Live indicator */}
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 mb-6">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          Live updates active
        </div>

        <button
          className="text-gray-500 text-sm underline hover:text-gray-700"
          onClick={handleLeave}
        >
          Leave queue
        </button>
      </div>
    </div>
  );
};

export default WaitingRoom;
